import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { objectToParams, paramsFromSearchString } from 'Shared/utils';

type FilterType = {
  [field: string]: Array<string>;
};

type FiltersQueryType = (
  initialState: FilterType,
  useUrlFilters?: boolean
) => {
  activeFilters: FilterType;
  hasActiveFilters: boolean;
  addValueToFilter(key: string, value: string): void;
  removeValueFromFilter(key: string, value: string): void;
  modifyFilter(key: string, value: Array<string>): void;
  modifyMultipleFilters(filterChanges: FilterType): void;
  clearAllFilters: () => void;
};

export const useFilters: FiltersQueryType = (
  initialState = {},
  useUrlFilters = false
) => {
  const [activeFilters, _setActiveFilters] = useState(initialState);
  const history = useHistory();

  useEffect(() => {
    const searchParams =
      history.location.search && history.location.search !== '?'
        ? paramsFromSearchString(history.location.search)
        : {};

    const currentActiveFilters = Object.keys(searchParams).reduce(
      (acc, curr) => {
        return {
          ...acc,
          [curr]: searchParams[curr]
            ? searchParams[curr].split(',').map(t => decodeURI(t))
            : [],
        };
      },
      {}
    );

    if (useUrlFilters) {
      _setActiveFilters(currentActiveFilters);
    }
  }, [useUrlFilters, history.location.search]);

  /**
   * Wrapper for the useState setter to remove...
   * - duplicate filters
   * - duplicate values in filters
   * - filters without any values.
   */
  const setActiveFilters = useCallback(
    (filters: FilterType) => {
      const activeFilters: Record<string, Array<string>> = Object.keys(
        filters
      ).reduce((acc, key) => {
        const nonEmptyValues = filters[key].filter(value => !!value);
        if (nonEmptyValues.length) {
          return { ...acc, [key]: Array.from(new Set(nonEmptyValues)) };
        }
        return acc;
      }, {});

      if (useUrlFilters) {
        history.replace({
          pathname: history.location.pathname,
          search: `?${objectToParams(activeFilters)}`,
        });
      }
      _setActiveFilters(activeFilters);
    },
    [history, useUrlFilters]
  );

  /**
   * Creates a filter and applies all values to it OR
   * replaces all values for an existing filter.
   *
   * @param key {string} Field to filter on
   * @param values {string[]} The entire set of values to filter
   */
  const modifyFilter = useCallback(
    (key: string, values?: Array<string>) => {
      if (values?.length) {
        setActiveFilters({ ...activeFilters, [key]: values });
      } else {
        let newFilters = { ...activeFilters };
        delete newFilters[key];
        setActiveFilters({ ...newFilters });
      }
    },
    [activeFilters, setActiveFilters]
  );

  /**
   * Adds one value to a filter's array of values.
   * If the filter does not currently exist, it is created.
   *
   * @param key {string} Field to filter on
   * @param value {string} A single value to add
   */
  const addValueToFilter = useCallback(
    (key: string, value: string) => {
      modifyFilter(key, [...(activeFilters[key] || []), value]);
    },
    [activeFilters, modifyFilter]
  );

  /**
   * Removes one value from a filter's array of values.
   *
   * @param key {string} Field to filter on
   * @param value {string} A single value to remove
   */
  const removeValueFromFilter = useCallback(
    (key: string, value: string) => {
      if (activeFilters.hasOwnProperty(key)) {
        modifyFilter(
          key,
          activeFilters[key].filter(val => val !== value)
        );
      }
    },
    [activeFilters, modifyFilter]
  );

  /**
   * Updates or creates multiple filters at once.
   * For any key given that does not exist, the filter will be created with all given values.
   * For any existing key, all existing values will be replaced with the given values.
   * Any existing filter that is not included will remain unchanged.
   *
   * @param filterChanges Collection of filters and values to modify
   */
  const modifyMultipleFilters = useCallback(
    (filterChanges: FilterType) => {
      setActiveFilters({ ...activeFilters, ...filterChanges });
    },
    [activeFilters, setActiveFilters]
  );

  /**
   * Clears all the filters.
   */
  const clearAllFilters = useCallback(() => setActiveFilters({}), [
    setActiveFilters,
  ]);

  return {
    hasActiveFilters: !!Object.keys(activeFilters).length,
    activeFilters,
    addValueToFilter,
    removeValueFromFilter,
    modifyFilter,
    modifyMultipleFilters,
    clearAllFilters,
  };
};
